import React, {useCallback, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {borderRadius, flexAbsoluteCenter, mb, pb} from "@pg-design/helpers-css";
import {BrandBuildingIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {OfferType} from "@pg-rp/common-types";

import {IRPStore} from "../../app/rp_reducer";
import {slickSlider} from "../../app/styles/slick_slider";
import {getAdjustedSliderSettings} from "../../app/utils/get_adjusted_slider_settings";
import {OfferBoxBase} from "../../atoms/OfferBoxBase";
import {propertyBoxLink, PropertyBoxVariantBCD} from "../../atoms/PropertyBox/PropertyBoxVariantBCD";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {createOfferLink} from "../../offer/helpers/create_offer_link";
import {getOfferTypeNamePlural} from "../../offer/helpers/OfferType";
import {createOfferListingAmplitudePendingDetailUrl} from "../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {hitGTMPropertySelectItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {IAmplitudeItemListContext} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackPropertySelectItemWithListContext} from "../../tracking/amplitude/track_property_select_item_with_list_context";
import {gtmOtherPropertyInOfferClick} from "../../tracking/google_tag_manager/gtm_event_other_property_in_offer";
import {useGTMEcommercePropertyListViewTracking} from "../../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";
import {slickSliderSettings} from "../constants/slick_slider_settings";
import {useOtherPropertiesInOfferAmplitudeViewItemListTracking} from "../hooks/use_other_properties_in_offer_amplitude_view_item_list_tracking";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyListProperty} from "../types/PropertyListProperty";
import {IRoomFilterFormValues, PropertyDetailOtherPropertiesFilters} from "./PropertyDetailOtherPropertiesFilters";

const LazySlider = React.lazy(() => import("@pg-mono/slick-slider"));

interface IProps {
    mainProperty: IPropertyDetail;
    properties: IPropertyListProperty[];
    count: number;
    offerId: number;
    propertyId: number;
    availableRooms: number[];
    rooms: number;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const PropertyDetailOtherPropertiesInOffer = (props: IProps) => {
    const theme = useTheme();
    const target = useResponsiveLinkTarget();
    const {updateUserPreferences} = useUserSearchPreferences();

    const requestState = useSelector((store: IRPStore) => store.property.otherPropertiesInOffer.request);

    const [slickTrackBoundingRect, setSlickTrackBoundingRect] = useState<DOMRect | null>(null);

    const isMounted = useIsMounted();
    const rooms = useSelector((store: IRPStore) => store.property.otherPropertiesInOffer.formValues.rooms) as IRoomFilterFormValues["rooms"];
    const allRoomsNumberText =
        rooms.lower === rooms.upper && rooms.lower !== "" && `${rooms.lower >= 5 ? "5" : rooms.lower}-pokojowe${rooms.lower >= 5 ? " i więcej" : ""}`;
    const isApartment = props.properties[0].offer.is_holiday_location || props.properties[0].offer.is_condohotel;

    const loader = (
        <div css={flexAbsoluteCenter} style={{height: "475px"}}>
            <Loader size="lg" />
        </div>
    );

    const trackingMeta = getTrackingMeta(props.properties?.[0]?.offer?.type);

    const {containerId, isWaitingForElement} = useGTMEcommercePropertyListViewTracking({
        offer: props.mainProperty.offer,
        properties: props.properties,
        listName: trackingMeta.listName,
        listId: trackingMeta.listId,
        useVisibilityTracking: true
    });

    const {itemListId: amplitudeItemListId, itemListName: amplitudeItemListName} = useOtherPropertiesInOfferAmplitudeViewItemListTracking({
        offer: props.mainProperty.offer,
        properties: props.properties,
        rooms,
        listName: trackingMeta.listName,
        requestState,
        isWaitingForElement
    });

    const offerLink = createOfferLink(props.properties[0].offer) + "#nieruchomosci";

    const onMorePropertiesClick = () => {
        updateUserPreferences({rooms_0: rooms.lower.toString(), rooms_1: rooms.upper.toString()});
        window.location.href = createOfferListingAmplitudePendingDetailUrl({
            url: offerLink,
            itemId: props.mainProperty.offer.id,
            itemListContext: props.amplitudeItemListContext
        });
    };

    const updateOtherPropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>, property: IPropertyListProperty) => {
        const anchor =
            event.currentTarget instanceof HTMLAnchorElement ? event.currentTarget : (event.currentTarget.querySelector("a[href]") as HTMLAnchorElement | null);

        if (!anchor) {
            return;
        }

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: property.offer.id,
            propertyId: property.id,
            itemListContext: props.amplitudeItemListContext
        });
    };

    const onOtherPropertyInOfferClick = useCallback(
        (property: IPropertyListProperty, index: number) => {
            gtmOtherPropertyInOfferClick({
                id: property.id,
                number: property.number,
                offerId: property.offer.id,
                offerName: property.offer.name,
                referrerPropertyId: props.propertyId
            });

            trackPropertySelectItemWithListContext({
                offer: props.mainProperty.offer,
                property,
                index,
                itemListContext: {itemListId: amplitudeItemListId, itemListName: amplitudeItemListName}
            });
            hitGTMPropertySelectItem({
                offer: props.mainProperty.offer,
                property,
                listName: trackingMeta.listName,
                listId: trackingMeta.listId
            });
        },
        [amplitudeItemListId, amplitudeItemListName, trackingMeta.listId, trackingMeta.listName, props.mainProperty.offer, props.propertyId]
    );

    const onSlickSliderInit = () => {
        const slickTrack = document.querySelector(".slick-track");

        if (slickTrack && !slickTrackBoundingRect) {
            setSlickTrackBoundingRect(slickTrack.getBoundingClientRect());
        }
    };

    const showMoreProperties = props.count > 10;
    const itemCount = props.properties.length + Number(showMoreProperties);
    const adjustedSliderSettings = useMemo(() => getAdjustedSliderSettings(slickSliderSettings, itemCount), [itemCount]);

    return (
        <>
            <Text
                as="h2"
                mb={2}
                variant="headline_4"
            >{`Inne ${isApartment ? "apartamenty" : getOfferTypeNamePlural(props.properties[0].offer.type)} w tej inwestycji`}</Text>

            <div>
                <PropertyDetailOtherPropertiesFilters
                    availableRooms={props.availableRooms}
                    offerId={props.offerId}
                    propertyId={props.propertyId}
                    rooms={props.rooms}
                />
            </div>

            <div css={propertyDetailSlickHolder} id={containerId} data-id={containerId}>
                {isMounted && requestState === RequestState.Success ? (
                    <React.Suspense fallback={loader}>
                        <LazySlider {...adjustedSliderSettings} onInit={onSlickSliderInit} css={propertySlider}>
                            {props.properties.map((otherProperty: IPropertyListProperty, index) => {
                                return (
                                    <PropertyBoxVariantBCD
                                        key={otherProperty.id}
                                        property={otherProperty}
                                        onClick={(property) => onOtherPropertyInOfferClick(property, index)}
                                        onLinkClick={updateOtherPropertyDetailLinkWithPendingContext}
                                        favouritePlacement="rp_favourite_other_apartments"
                                        favouriteLocation="property_page"
                                        amplitudeItemListContext={{itemListId: amplitudeItemListId, itemListName: amplitudeItemListName}}
                                        index={index}
                                    />
                                );
                            })}
                            {showMoreProperties && (
                                <OfferBoxBase css={moreProperties}>
                                    <a onClick={onMorePropertiesClick} className={propertyBoxLink} title={props.properties[0].offer.name} target={target}>
                                        <div css={propertyBoxInfoHolder} style={{height: slickTrackBoundingRect?.height}} />

                                        <div css={propertyDetailSlickLastSlide}>
                                            <BrandBuildingIcon css={mb(4)} size="12" wrapperColor={theme.colors.primary} wrapperSize="12" />

                                            <Text align="center" as="p" css={mb(4)} variant="headline_3">
                                                (+{props.count - 10} więcej)
                                            </Text>

                                            <Text align="center" variant="body_copy_1">
                                                Zobacz wszystkie {allRoomsNumberText} w&nbsp;tej&nbsp;inwestycji
                                            </Text>
                                        </div>
                                    </a>
                                </OfferBoxBase>
                            )}
                        </LazySlider>
                    </React.Suspense>
                ) : (
                    loader
                )}
            </div>
        </>
    );
};

const getTrackingMeta = (offerType: OfferType = OfferType.FLAT) => ({
    listName: `Inne ${getOfferTypeNamePlural(offerType)} w tej inwestycji`,
    listId: `inne_${getOfferTypeNamePlural(offerType)}_w_tej_inwestycji`
});

const propertyDetailSlickHolder = (theme: Theme) => css`
    ${slickSlider};

    .slick-list {
        ${pb(3)};

        .slick-track {
            display: flex;

            .slick-slide:not(:first-of-type) {
                padding: 0 1.6rem;
            }

            @media (min-width: ${theme.breakpoints.xs}) {
                .slick-slide {
                    padding: 0 1.6rem;
                }
            }
        }
    }

    .slick-next {
        &::before {
            right: -0.8rem;
        }
    }

    .slick-prev {
        &::before {
            left: -0.8rem;
            transform: none;
        }
    }
`;

const propertyDetailSlickLastSlide = css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const propertyBoxInfoHolder = css`
    padding: 3.44rem 3.2rem;
`;

const moreProperties = css`
    ${borderRadius(2)};
    overflow: hidden;
`;

const propertySlider = css`
    & .slick-slide {
        height: inherit;

        & > div {
            height: 100%;
        }
    }
`;
import React, {useCallback, useMemo, useState} from "react";
import {useSelector} from "react-redux";
import {css, Theme, useTheme} from "@emotion/react";

import {borderRadius, flexAbsoluteCenter, mb, pb} from "@pg-design/helpers-css";
import {BrandBuildingIcon} from "@pg-design/icons-module";
import {Loader} from "@pg-design/loader-module";
import {Text} from "@pg-design/text-module";
import {useIsMounted} from "@pg-mono/hooks";
import {RequestState} from "@pg-mono/request-state";
import {OfferType} from "@pg-rp/common-types";

import {IRPStore} from "../../app/rp_reducer";
import {slickSlider} from "../../app/styles/slick_slider";
import {getAdjustedSliderSettings} from "../../app/utils/get_adjusted_slider_settings";
import {OfferBoxBase} from "../../atoms/OfferBoxBase";
import {propertyBoxLink, PropertyBoxVariantBCD} from "../../atoms/PropertyBox/PropertyBoxVariantBCD";
import {useResponsiveLinkTarget} from "../../hooks/use_responsive_link_target";
import {createOfferLink} from "../../offer/helpers/create_offer_link";
import {getOfferTypeNamePlural} from "../../offer/helpers/OfferType";
import {createOfferListingAmplitudePendingDetailUrl} from "../../offer/list/helpers/offer_listing_amplitude_item_list_id";
import {hitGTMPropertySelectItem} from "../../offer/tracking/ecommerce_events/property/hit_gtm_property_select_item";
import {IAmplitudeItemListContext} from "../../tracking/amplitude/get_amplitude_view_item_event_properties";
import {trackPropertySelectItemWithListContext} from "../../tracking/amplitude/track_property_select_item_with_list_context";
import {gtmOtherPropertyInOfferClick} from "../../tracking/google_tag_manager/gtm_event_other_property_in_offer";
import {useGTMEcommercePropertyListViewTracking} from "../../tracking/hooks/use_gtm_ecommerce_property_list_view_tracking";
import {useUserSearchPreferences} from "../../user/hooks/use_user_search_preferences";
import {slickSliderSettings} from "../constants/slick_slider_settings";
import {useOtherPropertiesInOfferAmplitudeViewItemListTracking} from "../hooks/use_other_properties_in_offer_amplitude_view_item_list_tracking";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {IPropertyListProperty} from "../types/PropertyListProperty";
import {IRoomFilterFormValues, PropertyDetailOtherPropertiesFilters} from "./PropertyDetailOtherPropertiesFilters";

const LazySlider = React.lazy(() => import("@pg-mono/slick-slider"));

interface IProps {
    mainProperty: IPropertyDetail;
    properties: IPropertyListProperty[];
    count: number;
    offerId: number;
    propertyId: number;
    availableRooms: number[];
    rooms: number;
    amplitudeItemListContext?: IAmplitudeItemListContext | null;
}

export const PropertyDetailOtherPropertiesInOffer = (props: IProps) => {
    const theme = useTheme();
    const target = useResponsiveLinkTarget();
    const {updateUserPreferences} = useUserSearchPreferences();

    const requestState = useSelector((store: IRPStore) => store.property.otherPropertiesInOffer.request);

    const [slickTrackBoundingRect, setSlickTrackBoundingRect] = useState<DOMRect | null>(null);

    const isMounted = useIsMounted();
    const rooms = useSelector((store: IRPStore) => store.property.otherPropertiesInOffer.formValues.rooms) as IRoomFilterFormValues["rooms"];
    const allRoomsNumberText =
        rooms.lower === rooms.upper && rooms.lower !== "" && `${rooms.lower >= 5 ? "5" : rooms.lower}-pokojowe${rooms.lower >= 5 ? " i więcej" : ""}`;
    const isApartment = props.properties[0].offer.is_holiday_location || props.properties[0].offer.is_condohotel;

    const loader = (
        <div css={flexAbsoluteCenter} style={{height: "475px"}}>
            <Loader size="lg" />
        </div>
    );

    const trackingMeta = getTrackingMeta(props.properties?.[0]?.offer?.type);

    const {containerId, isWaitingForElement} = useGTMEcommercePropertyListViewTracking({
        offer: props.mainProperty.offer,
        properties: props.properties,
        listName: trackingMeta.listName,
        listId: trackingMeta.listId,
        useVisibilityTracking: true
    });

    const {itemListId: amplitudeItemListId, itemListName: amplitudeItemListName} = useOtherPropertiesInOfferAmplitudeViewItemListTracking({
        offer: props.mainProperty.offer,
        properties: props.properties,
        rooms,
        listName: trackingMeta.listName,
        requestState,
        isWaitingForElement
    });

    const offerLink = createOfferLink(props.properties[0].offer) + "#nieruchomosci";

    const onMorePropertiesClick = () => {
        updateUserPreferences({rooms_0: rooms.lower.toString(), rooms_1: rooms.upper.toString()});
        window.location.href = createOfferListingAmplitudePendingDetailUrl({
            url: offerLink,
            itemId: props.mainProperty.offer.id,
            itemListContext: props.amplitudeItemListContext
        });
    };

    const updateOtherPropertyDetailLinkWithPendingContext = (event: React.MouseEvent<HTMLElement>, property: IPropertyListProperty) => {
        const anchor =
            event.currentTarget instanceof HTMLAnchorElement ? event.currentTarget : (event.currentTarget.querySelector("a[href]") as HTMLAnchorElement | null);

        if (!anchor) {
            return;
        }

        anchor.href = createOfferListingAmplitudePendingDetailUrl({
            url: anchor.href,
            itemId: property.offer.id,
            propertyId: property.id,
            itemListContext: props.amplitudeItemListContext
        });
    };

    const onOtherPropertyInOfferClick = useCallback(
        (property: IPropertyListProperty, index: number) => {
            gtmOtherPropertyInOfferClick({
                id: property.id,
                number: property.number,
                offerId: property.offer.id,
                offerName: property.offer.name,
                referrerPropertyId: props.propertyId
            });

            trackPropertySelectItemWithListContext({
                offer: props.mainProperty.offer,
                property,
                index,
                itemListContext: {itemListId: amplitudeItemListId, itemListName: amplitudeItemListName}
            });
            hitGTMPropertySelectItem({
                offer: props.mainProperty.offer,
                property,
                listName: trackingMeta.listName,
                listId: trackingMeta.listId
            });
        },
        [amplitudeItemListId, amplitudeItemListName, trackingMeta.listId, trackingMeta.listName, props.mainProperty.offer, props.propertyId]
    );

    const onSlickSliderInit = () => {
        const slickTrack = document.querySelector(".slick-track");

        if (slickTrack && !slickTrackBoundingRect) {
            setSlickTrackBoundingRect(slickTrack.getBoundingClientRect());
        }
    };

    const showMoreProperties = props.count > 10;
    const itemCount = props.properties.length + Number(showMoreProperties);
    const adjustedSliderSettings = useMemo(() => getAdjustedSliderSettings(slickSliderSettings, itemCount), [itemCount]);

    return (
        <>
            <Text
                as="h2"
                mb={2}
                variant="headline_4"
            >{`Inne ${isApartment ? "apartamenty" : getOfferTypeNamePlural(props.properties[0].offer.type)} w tej inwestycji`}</Text>

            <div>
                <PropertyDetailOtherPropertiesFilters
                    availableRooms={props.availableRooms}
                    offerId={props.offerId}
                    propertyId={props.propertyId}
                    rooms={props.rooms}
                />
            </div>

            <div css={propertyDetailSlickHolder} id={containerId} data-id={containerId}>
                {isMounted && requestState === RequestState.Success ? (
                    <React.Suspense fallback={loader}>
                        <LazySlider {...adjustedSliderSettings} onInit={onSlickSliderInit} css={propertySlider}>
                            {props.properties.map((otherProperty: IPropertyListProperty, index) => {
                                return (
                                    <PropertyBoxVariantBCD
                                        key={otherProperty.id}
                                        property={otherProperty}
                                        onClick={(property) => onOtherPropertyInOfferClick(property, index)}
                                        onLinkClick={updateOtherPropertyDetailLinkWithPendingContext}
                                        favouritePlacement="rp_favourite_other_apartments"
                                        favouriteLocation="property_page"
                                        amplitudeItemListContext={{itemListId: amplitudeItemListId, itemListName: amplitudeItemListName}}
                                        index={index}
                                    />
                                );
                            })}
                            {showMoreProperties && (
                                <OfferBoxBase css={moreProperties}>
                                    <a onClick={onMorePropertiesClick} className={propertyBoxLink} title={props.properties[0].offer.name} target={target}>
                                        <div css={propertyBoxInfoHolder} style={{height: slickTrackBoundingRect?.height}} />

                                        <div css={propertyDetailSlickLastSlide}>
                                            <BrandBuildingIcon css={mb(4)} size="12" wrapperColor={theme.colors.primary} wrapperSize="12" />

                                            <Text align="center" as="p" css={mb(4)} variant="headline_3">
                                                (+{props.count - 10} więcej)
                                            </Text>

                                            <Text align="center" variant="body_copy_1">
                                                Zobacz wszystkie {allRoomsNumberText} w&nbsp;tej&nbsp;inwestycji
                                            </Text>
                                        </div>
                                    </a>
                                </OfferBoxBase>
                            )}
                        </LazySlider>
                    </React.Suspense>
                ) : (
                    loader
                )}
            </div>
        </>
    );
};

const getTrackingMeta = (offerType: OfferType = OfferType.FLAT) => ({
    listName: `Inne ${getOfferTypeNamePlural(offerType)} w tej inwestycji`,
    listId: `inne_${getOfferTypeNamePlural(offerType)}_w_tej_inwestycji`
});

const propertyDetailSlickHolder = (theme: Theme) => css`
    ${slickSlider};

    .slick-list {
        ${pb(3)};

        .slick-track {
            display: flex;

            .slick-slide:not(:first-of-type) {
                padding: 0 1.6rem;
            }

            @media (min-width: ${theme.breakpoints.xs}) {
                .slick-slide {
                    padding: 0 1.6rem;
                }
            }
        }
    }

    .slick-next {
        &::before {
            right: -0.8rem;
        }
    }

    .slick-prev {
        &::before {
            left: -0.8rem;
            transform: none;
        }
    }
`;

const propertyDetailSlickLastSlide = css`
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: #fff;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
`;

const propertyBoxInfoHolder = css`
    padding: 3.44rem 3.2rem;
`;

const moreProperties = css`
    ${borderRadius(2)};
    overflow: hidden;
`;

const propertySlider = css`
    & .slick-slide {
        height: inherit;

        & > div {
            height: 100%;
        }
    }
`;
